// HUB-UI: workspace dashboard, job posting modals/grids, applicant pipeline and directory admin.
import { useCallback, useState, type CSSProperties } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Button, Chip, Stack, Typography } from '@mui/material';
import { useAppSelector } from '../../app/store';
import { useCan } from '../../core/authorization/PermissionProvider';
import { publicApi, type Paged, type OrganizationCard } from '../public/publicApi';
import { read, write, type Applicant, type Category, type Job, type Values } from './hubApi';
import {
  EntityDialog,
  Fields,
  EntityGrid,
  errorMessage,
  HubEmpty,
  HubPager,
  HubSection,
  HubStat,
  MiniBars,
  ProductMark,
  productMark,
  HubTitle,
  LoadState,
  useHubLoad,
  type Field,
} from './HubUi';
import Add from '@mui/icons-material/Add';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined';
import WorkOutline from '@mui/icons-material/WorkOutline';
import EventNoteOutlined from '@mui/icons-material/EventNoteOutlined';
import SwapHorizOutlined from '@mui/icons-material/SwapHorizOutlined';
import AppsOutlined from '@mui/icons-material/AppsOutlined';
import ApartmentOutlined from '@mui/icons-material/ApartmentOutlined';
import KeyOutlined from '@mui/icons-material/KeyOutlined';
import { CountUp } from '../../premium/motion';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ProfileLocationSearch } from './ProfileLocationSearch';
import { authApi, authorizationApi } from '../../core/api/client';

export function WorkspaceDashboardPage() {
  const { organization, launcher, snapshot, profile } = useAppSelector((state) => state.session);
  const canCompany = useCan('platform.company', 'read');
  const canJobs = useCan('platform.jobs', 'read');
  const destinations = useHubLoad(useCallback(() => authApi.destinations(), []));
  const admin = snapshot.isSuperAdmin === true;
  const companies = destinations.data?.organizations.filter((item) => item.code !== 'COMMUNITY') ?? null;
  const appsPerCompany = companies?.map((item) => item.destinations.length) ?? [];
  const totalApps = appsPerCompany.reduce((sum, value) => sum + value, 0);
  // Access by product across every company: real counts, drawn as one segmented bar.
  const byProduct = new Map<string, { name: string; count: number }>();
  companies?.forEach((item) =>
    item.destinations.forEach((destination) => {
      const entry = byProduct.get(destination.code) ?? { name: destination.displayName, count: 0 };
      entry.count += 1;
      byProduct.set(destination.code, entry);
    }),
  );
  const segments = Array.from(byProduct.entries()).sort((a, b) => b[1].count - a[1].count);
  return (
    <div className="hub-dash">
      <section className="hub-dash-hero">
        <div className="hub-dash-hero-text">
          <h1>{admin ? 'Platform administration' : `Hello, ${profile.firstName}`}</h1>
          <span className="hub-dash-chip">
            <StorefrontOutlined sx={{ fontSize: 16 }} aria-hidden />
            {organization.name}
          </span>
          <div className="hub-dash-actions">
            {canCompany && (
              <Button component={Link} to="/app/organization-profiles" variant="contained" disableElevation startIcon={<StorefrontOutlined />}>
                Organization profiles
              </Button>
            )}
            {canJobs && (
              <Button component={Link} to="/app/jobs" className="hub-soft-btn" startIcon={<WorkOutline />}>
                Jobs
              </Button>
            )}
            <Button component={Link} to="/me" className="hub-soft-btn" startIcon={<EventNoteOutlined />}>
              My orders
            </Button>
            <Button component={Link} to="/switch" className="hub-soft-btn" startIcon={<SwapHorizOutlined />}>
              Switch
            </Button>
          </div>
        </div>
        <svg className="hub-dash-art" viewBox="0 0 240 200" aria-hidden="true">
          <rect className="a" x="96" y="16" width="124" height="78" rx="18" />
          <rect className="b" x="28" y="60" width="128" height="80" rx="18" />
          <rect className="c" x="116" y="112" width="104" height="70" rx="18" />
          <circle cx="58" cy="88" r="11" />
          <path d="M80 84h52M80 100h34M48 120h92" />
        </svg>
      </section>

      <div className="hub-stats hub-rise">
        <HubStat
          icon={AppsOutlined}
          label="Products here"
          value={<CountUp value={launcher.length} duration={700} />}
          figure={
            launcher.length > 0 ? (
              <span className="hub-mark-row">
                {launcher.slice(0, 6).map((product) => (
                  <ProductMark key={product.code} code={product.code} size={26} />
                ))}
              </span>
            ) : undefined
          }
        />
        <HubStat
          icon={ApartmentOutlined}
          label="Companies"
          value={companies ? <CountUp value={companies.length} duration={700} /> : '-'}
          figure={
            companies && companies.length > 1 ? (
              <MiniBars values={appsPerCompany} labels={companies.map((item) => item.displayName || item.name)} />
            ) : undefined
          }
        />
        <HubStat
          icon={KeyOutlined}
          label="App access"
          value={companies ? <CountUp value={totalApps} duration={700} /> : '-'}
          figure={
            totalApps > 0 ? (
              <span
                className="hub-segbar"
                role="img"
                aria-label={segments.map(([, entry]) => `${entry.name} ${entry.count}`).join(', ')}
              >
                {segments.map(([code, entry]) => (
                  <span
                    key={code}
                    title={`${entry.name}: ${entry.count}`}
                    style={{ flexGrow: entry.count, '--mark': productMark(code).hue } as CSSProperties}
                  />
                ))}
              </span>
            ) : undefined
          }
        />
      </div>

      <HubSection title="Products">
        {launcher.length === 0 ? (
          <HubEmpty>No product is enabled for this company yet.</HubEmpty>
        ) : (
          <div className="hub-products hub-rise">
            {launcher.map((product) => (
              <Link
                className="hub-product"
                key={product.code}
                to={`/launch/${product.code.toLowerCase()}`}
                style={{ '--mark': productMark(product.code).hue } as CSSProperties}
              >
                <ProductMark code={product.code} size={48} />
                <strong>{product.displayName}</strong>
                <span className="hub-product-go">
                  Open <ArrowForward sx={{ fontSize: 16 }} aria-hidden />
                </span>
              </Link>
            ))}
          </div>
        )}
      </HubSection>

      <HubSection title="Your companies">
        <LoadState {...destinations} />
        {companies && companies.length === 0 && <HubEmpty>You can use your personal account and explore companies.</HubEmpty>}
        {companies && companies.length > 0 && (
          <ul className="hub-company-list hub-rise">
            {companies.map((row) => {
              const name = row.displayName || row.name;
              const current = row.id === organization.id;
              return (
                <li key={row.id} className={current ? 'is-current' : undefined}>
                  <span className="hub-company-avatar" aria-hidden>
                    {name
                      .split(/\s+/)
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((word) => word[0]?.toUpperCase())
                      .join('')}
                  </span>
                  <div className="hub-company-main">
                    <strong>{name}</strong>
                    <span className="hub-company-apps">
                      {row.destinations.map((destination) => (
                        <span
                          key={destination.code}
                          className="hub-app-chip"
                          style={{ '--mark': productMark(destination.code).hue } as CSSProperties}
                        >
                          {destination.displayName}
                        </span>
                      ))}
                    </span>
                  </div>
                  {current ? (
                    <span className="hub-pill ok">Active</span>
                  ) : (
                    <Button size="small" component={Link} to="/switch" className="hub-soft-btn">
                      Open
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </HubSection>
    </div>
  );
}
const options = (values: string[]) => values.map((value) => ({ value, label: value }));
export const jobFields: Field[] = [
  ...['Al', 'En', 'It'].flatMap((language) => [
    { key: `title${language}`, label: `Title (${language.toUpperCase()})`, maxLength: 200 },
    ...['description', 'requirements', 'benefits'].map((key) => ({
      key: `${key}${language}`,
      label: `${key[0].toUpperCase()}${key.slice(1)} (${language.toUpperCase()})`,
      type: 'multiline' as const,
      maxLength: 8000,
    })),
  ]),
  { key: 'roleCode', label: 'Job role', type: 'select', required: true, options: [] },
  { key: 'productCode', label: 'Product', type: 'select', options: [] },
  {
    key: 'employmentType',
    label: 'Employment type',
    type: 'select',
    required: true,
    options: options(['FullTime', 'PartTime', 'Freelance', 'ChairRental', 'Seasonal', 'Internship']),
  },
  {
    key: 'experienceLevel',
    label: 'Experience level',
    type: 'select',
    required: true,
    options: options(['Entry', 'Junior', 'Mid', 'Senior']),
  },
  { key: 'addressLine', label: 'Address', maxLength: 300 },
  { key: 'city', label: 'City', required: true, maxLength: 100, readOnly: true, helper: 'Filled automatically when you select a location.' },
  { key: 'latitude', label: 'Latitude', type: 'number', min: -90, max: 90 },
  { key: 'longitude', label: 'Longitude', type: 'number', min: -180, max: 180 },
  { key: 'salaryMin', label: 'Minimum salary', type: 'number', min: 0 },
  { key: 'salaryMax', label: 'Maximum salary', type: 'number', min: 0 },
  { key: 'salaryCurrency', label: 'Salary currency', maxLength: 3 },
  { key: 'salaryPeriod', label: 'Salary period', type: 'select', options: options(['Hour', 'Month', 'Year']) },
  { key: 'showSalary', label: 'Show salary publicly', type: 'boolean' },
  { key: 'closesOn', label: 'Closing date', type: 'date' },
];
const jobSections = [
  { title: 'Position', hint: 'Choose the role and basic details for this posting.', keys: ['roleCode', 'productCode', 'employmentType', 'experienceLevel'] },
  { title: 'Location', hint: 'Tell applicants where the work takes place.', keys: ['city', 'addressLine'] },
  { title: 'Pay and deadline', hint: 'Salary is optional. Set a closing date if applications should end automatically.', keys: ['salaryMin', 'salaryMax', 'salaryCurrency', 'salaryPeriod', 'showSalary', 'closesOn'] },
];
export function validateJob(value: Values): string | null {
  if (!['titleAl', 'titleEn', 'titleIt'].some((key) => String(value[key] ?? '').trim()))
    return 'Enter a title in at least one language.';
  if (
    value.salaryMin !== null &&
    value.salaryMax !== null &&
    value.salaryMin !== undefined &&
    value.salaryMax !== undefined &&
    Number(value.salaryMax) < Number(value.salaryMin)
  )
    return 'Maximum salary must be at least the minimum.';
  if ((value.salaryMin != null || value.salaryMax != null) && (!value.salaryCurrency || !value.salaryPeriod))
    return 'Choose a currency and period for the salary.';
  return null;
}
export function WorkspaceJobsPage() {
  const [page, setPage] = useState(1);
  const [edit, setEdit] = useState<Values | null>(null);
  const [jobLanguage, setJobLanguage] = useState<'Al' | 'En' | 'It'>('En');
  const [pending, setPending] = useState<{ row: Job; action: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = useHubLoad(
    useCallback((signal: AbortSignal) => read<Paged<Job>>('/jobs', { page, pageSize: 20 }, signal), [page]),
  );
  const taxonomy = useHubLoad(useCallback(() => publicApi.jobRoles('en'), []));
  const launcher = useAppSelector((state) => state.session.launcher);
  const canCreate = useCan('platform.jobs', 'create');
  const canUpdate = useCan('platform.jobs', 'update');
  const canDelete = useCan('platform.jobs', 'delete');
  return (
    <>
      <HubTitle
        title="Job postings"
        action={
          <Button
            variant="contained"
            startIcon={<Add />}
            disabled={!canCreate || !taxonomy.data || !load.data}
            onClick={() =>
              setEdit({
                titleAl: '',
                titleEn: '',
                titleIt: '',
                roleCode: '',
                employmentType: 'FullTime',
                experienceLevel: 'Entry',
                city: '',
                showSalary: false,
              })
            }
          >
            Create job
          </Button>
        }
      />
      <LoadState {...load} />
      <LoadState {...taxonomy} />
      {error && <Alert severity="error">{error}</Alert>}
      {load.data && (
        <>
          <EntityGrid
            rows={load.data.items}
            rowKey={(row) => row.id}
            columns={[
              { label: 'Position', render: (row) => row.titleEn || row.titleAl || row.titleIt },
              { label: 'City', render: (row) => row.city },
              { label: 'Employment', render: (row) => row.employmentType },
              {
                label: 'Status',
                render: (row) => (
                  <Chip
                    size="small"
                    color={row.status === 'Open' ? 'success' : row.status === 'Paused' ? 'warning' : 'default'}
                    label={row.status}
                  />
                ),
              },
            ]}
            actions={(row) => (
              <>
                <Button size="small" variant="outlined" component={Link} to={`/app/jobs/${row.id}/applicants`}>
                  Applicants
                </Button>
                <Button size="small" disabled={!canUpdate || !taxonomy.data} onClick={() => setEdit(row)}>
                  Edit
                </Button>
                {['Draft', 'Paused'].includes(row.status) && (
                  <Button size="small" disabled={!canUpdate} onClick={() => setPending({ row, action: 'publish' })}>
                    Publish
                  </Button>
                )}
                {row.status === 'Open' && (
                  <Button size="small" disabled={!canUpdate} onClick={() => setPending({ row, action: 'pause' })}>
                    Pause
                  </Button>
                )}
                {row.status !== 'Closed' && (
                  <Button size="small" disabled={!canUpdate} onClick={() => setPending({ row, action: 'close' })}>
                    Close
                  </Button>
                )}
                <Button size="small" disabled={!canDelete} color="error" onClick={() => setPending({ row, action: 'delete' })}>
                  Delete
                </Button>
              </>
            )}
            empty="No job postings yet. Create one to start receiving applications."
          />
          <HubPager page={page} pageSize={load.data.pageSize} total={load.data.total} onPage={setPage} />
        </>
      )}
      {edit && (
        <EntityDialog
          title={edit.id ? 'Edit job' : 'Create job'}
          value={edit}
          close={() => setEdit(null)}
          validate={validateJob}
          fields={jobFields.map((field) =>
            field.key === 'roleCode'
              ? { ...field, options: taxonomy.data?.map((role) => ({ value: role.code, label: role.name })) }
              : field.key === 'productCode'
                ? {
                    ...field,
                    options: launcher.map((product) => ({ value: product.code, label: product.displayName })),
                  }
                : field,
          )}
          renderFields={(value, onChange, disabled) => {
            const fields = jobFields.map((field) =>
              field.key === 'roleCode'
                ? { ...field, options: taxonomy.data?.map((role) => ({ value: role.code, label: role.name })) }
                : field.key === 'productCode'
                  ? { ...field, options: launcher.map((product) => ({ value: product.code, label: product.displayName })) }
                  : field,
            );
            return (
              <div className="hub-job-form">
                {jobSections.slice(0, 2).map((section) => (
                  <section className="hub-job-section" key={section.title}>
                    <div className="hub-job-section-heading"><h3>{section.title}</h3><p>{section.hint}</p></div>
                    <Fields fields={fields.filter((field) => section.keys.includes(field.key))} value={value} onChange={onChange} disabled={disabled} />
                    {section.title === 'Location' && <ProfileLocationSearch mode="job" value={value} onChange={onChange} disabled={disabled} />}
                  </section>
                ))}
                <section className="hub-job-section">
                  <div className="hub-job-section-heading"><h3>Job description</h3><p>Add a title in at least one language. Other text can be filled in as needed.</p></div>
                  <div className="hub-job-language-tabs" role="group" aria-label="Job description language">
                    {(['Al', 'En', 'It'] as const).map((language) => (
                      <Button
                        key={language}
                        size="small"
                        className={jobLanguage === language ? 'is-active' : undefined}
                        aria-pressed={jobLanguage === language}
                        onClick={() => setJobLanguage(language)}
                      >
                        {language.toUpperCase()}
                      </Button>
                    ))}
                  </div>
                  <div className="hub-job-language">
                    <Fields fields={fields.filter((field) => field.key.endsWith(jobLanguage))} value={value} onChange={onChange} disabled={disabled} />
                  </div>
                </section>
                <section className="hub-job-section">
                  <div className="hub-job-section-heading"><h3>{jobSections[2].title}</h3><p>{jobSections[2].hint}</p></div>
                  <Fields fields={fields.filter((field) => jobSections[2].keys.includes(field.key))} value={value} onChange={onChange} disabled={disabled} />
                </section>
              </div>
            );
          }}
          save={async (value) => {
            const body = {
              ...Object.fromEntries(
                jobFields.map((field) => [field.key, value[field.key] === '' ? null : (value[field.key] ?? null)]),
              ),
              rowVersion: edit.rowVersion,
            };
            await write(`/jobs${edit.id ? `/${edit.id}` : ''}`, body, edit.id ? 'put' : 'post');
            load.reload();
          }}
        />
      )}
      <ConfirmDialog
        open={!!pending}
        title={pending ? `${pending.action[0].toUpperCase()}${pending.action.slice(1)} job?` : ''}
        message="This changes the availability of this position."
        busy={busy}
        onClose={() => setPending(null)}
        onConfirm={async () => {
          if (!pending) return;
          setBusy(true);
          setError(null);
          try {
            await write(
              `/jobs/${pending.row.id}${pending.action === 'delete' ? '' : `/${pending.action}`}`,
              { rowVersion: pending.row.rowVersion },
              pending.action === 'delete' ? 'delete' : 'post',
            );
            setPending(null);
            load.reload();
          } catch (caught) {
            setError(errorMessage(caught));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}
const stages = ['New', 'Screening', 'Interview', 'Trial', 'Offer', 'Hired', 'Rejected', 'Withdrawn'];
export function ApplicantsPage() {
  const { id = '' } = useParams();
  const load = useHubLoad(
    useCallback((signal: AbortSignal) => read<Applicant[]>(`/jobs/${id}/applications`, undefined, signal), [id]),
  );
  const [edit, setEdit] = useState<{ row: Applicant; action: 'stage' | 'notes' | 'hire' } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const roles = useHubLoad(useCallback(() => authorizationApi.roles(), []));
  const launcher = useAppSelector((state) => state.session.launcher);
  const canUpdate = useCan('platform.jobs', 'update');
  const canHire = useCan('platform.memberships', 'create');
  return (
    <>
      <HubTitle
        title="Applicants"
        action={
          <Button component={Link} to="/app/jobs" startIcon={<ArrowBack />}>
            Back to jobs
          </Button>
        }
      />
      <LoadState {...load} />
      {error && <Alert severity="error">{error}</Alert>}
      {load.data && (
        <div className="hub-pipeline">
          {stages.map((stage) => (
            <section key={stage} aria-label={stage}>
              <h2>
                {stage} <span>{load.data?.filter((row) => row.stage === stage).length}</span>
              </h2>
              {load.data
                ?.filter((row) => row.stage === stage)
                .map((row) => (
                  <article key={row.id}>
                    <strong>{row.name}</strong>
                    <Typography variant="body2" color="text.secondary">
                      {row.email}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Applied {new Date(row.submittedAt).toLocaleDateString()}
                    </Typography>
                    <Stack gap={0.5} sx={{ mt: 1.5, '& .MuiButton-root': { justifyContent: 'flex-start' } }}>
                      <Button
                        disabled={!canUpdate || ['Hired', 'Withdrawn'].includes(row.stage)}
                        onClick={() => setEdit({ row, action: 'stage' })}
                      >
                        Stage & rating
                      </Button>
                      <Button disabled={!canUpdate} onClick={() => setEdit({ row, action: 'notes' })}>
                        Add private note
                      </Button>
                      <Button
                        disabled={busy}
                        onClick={async () => {
                          setBusy(true);
                          setError(null);
                          try {
                            const blob = (
                              await (
                                await import('../../core/http/client')
                              )
                                .http()
                                .get(`/applications/${row.id}/cv`, { responseType: 'blob', skipErrorToast: true })
                            ).data;
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = 'applicant-cv';
                            link.click();
                            window.setTimeout(() => URL.revokeObjectURL(url), 1000);
                          } catch (caught) {
                            setError(errorMessage(caught));
                          } finally {
                            setBusy(false);
                          }
                        }}
                      >
                        Download CV
                      </Button>
                      <Button
                        disabled={
                          !canUpdate ||
                          !canHire ||
                          !roles.data ||
                          ['Hired', 'Rejected', 'Withdrawn'].includes(row.stage)
                        }
                        onClick={() => setEdit({ row, action: 'hire' })}
                      >
                        Hire
                      </Button>
                    </Stack>
                  </article>
                ))}
            </section>
          ))}
        </div>
      )}
      {edit && (
        <EntityDialog
          title={
            edit.action === 'hire'
              ? 'Hire applicant'
              : edit.action === 'notes'
                ? 'Private applicant note'
                : 'Stage & rating'
          }
          value={edit.action === 'stage' ? { stage: edit.row.stage, rating: edit.row.rating } : { text: '' }}
          close={() => setEdit(null)}
          fields={
            edit.action === 'stage'
              ? [
                  {
                    key: 'stage',
                    label: 'Stage',
                    type: 'select',
                    required: true,
                    options: options(stages.filter((stage) => !['Hired', 'Withdrawn'].includes(stage))),
                  },
                  { key: 'rating', label: 'Rating (1–5)', type: 'number', min: 1, max: 5 },
                ]
              : edit.action === 'notes'
                ? [
                    {
                      key: 'text',
                      label: 'Note (visible to staff only)',
                      required: true,
                      type: 'multiline',
                      maxLength: 2000,
                    },
                  ]
                : [
                    {
                      key: 'roleId',
                      label: 'Membership role',
                      type: 'select',
                      required: true,
                      options: roles.data?.map((role) => ({ value: role.id, label: role.name })),
                    },
                    ...launcher
                      .filter((product) => product.code !== 'PLATFORM')
                      .map((product) => ({ key: product.code, label: product.displayName, type: 'boolean' as const })),
                  ]
          }
          save={async (value) => {
            await write(
              `/applications/${edit.row.id}/${edit.action}`,
              edit.action === 'hire'
                ? {
                    roleIds: [value.roleId],
                    productCodes: [
                      'PLATFORM',
                      ...launcher
                        .filter((product) => product.code !== 'PLATFORM' && value[product.code] === true)
                        .map((product) => product.code),
                    ],
                  }
                : value,
              edit.action === 'stage' ? 'put' : 'post',
            );
            load.reload();
          }}
        />
      )}
    </>
  );
}
export function DirectoryAdminPage() {
  const admin = useAppSelector(
    (state) => state.session.snapshot.isSuperAdmin === true,
  );
  const canUpdate = useCan('platform.directory', 'update');
  return admin && canUpdate ? (
    <DirectoryAdminContent />
  ) : (
    <Alert severity="warning">Directory administration requires the Platform administrator role.</Alert>
  );
}
function DirectoryAdminContent() {
  const load = useHubLoad(
    useCallback((signal: AbortSignal) => read<Category[]>('/directory/categories', undefined, signal), []),
  );
  const [edit, setEdit] = useState<Category | null>(null);
  const [company, setCompany] = useState<{ row: OrganizationCard; action: 'featured' | 'verified' } | null>(null);
  const [page, setPage] = useState(1);
  const organizations = useHubLoad(useCallback(() => publicApi.organizations('en', { page, pageSize: 20 }), [page]));
  return (
    <>
      <HubTitle title="Directory administration" />
      <LoadState {...load} />
      {load.data && (
        <EntityGrid
          rows={load.data}
          rowKey={(row) => row.code}
          columns={[
            { label: 'Code', render: (row) => row.code },
            { label: 'Group', render: (row) => row.parentCode },
            { label: 'AL', render: (row) => row.nameAl },
            { label: 'EN', render: (row) => row.nameEn },
            { label: 'IT', render: (row) => row.nameIt },
            { label: 'Order', render: (row) => row.sortOrder },
            { label: 'Active', render: (row) => (row.isActive ? 'Yes' : 'No') },
          ]}
          actions={(row) => (
            <Button size="small" onClick={() => setEdit(row)}>
              Edit
            </Button>
          )}
        />
      )}
      {edit && (
        <EntityDialog
          title={`Edit category · ${edit.code}`}
          value={edit}
          close={() => setEdit(null)}
          fields={[
            ...['Al', 'En', 'It'].map((language) => ({
              key: `name${language}`,
              label: `Name (${language.toUpperCase()})`,
              required: true,
              maxLength: 150,
            })),
            { key: 'icon', label: 'Icon name', maxLength: 50 },
            { key: 'sortOrder', label: 'Sort order', type: 'number' },
            { key: 'isActive', label: 'Active', type: 'boolean' },
          ]}
          save={async (value) => {
            await write('/directory/categories', {
              categories: [
                {
                  code: edit.code,
                  nameAl: value.nameAl,
                  nameEn: value.nameEn,
                  nameIt: value.nameIt,
                  icon: value.icon,
                  sortOrder: value.sortOrder,
                  isActive: value.isActive,
                },
              ],
            });
            load.reload();
          }}
        />
      )}
      <HubSection title="Listed companies">
      <LoadState {...organizations} />
      {organizations.data && (
        <>
          <EntityGrid
            rows={organizations.data.items}
            rowKey={(row) => row.id}
            columns={[
              { label: 'Company', render: (row) => row.name },
              { label: 'City', render: (row) => row.city },
              { label: 'Featured', render: (row) => (row.isFeatured ? 'Yes' : 'No') },
              { label: 'Verified', render: (row) => (row.isVerified ? 'Yes' : 'No') },
            ]}
            actions={(row) => (
              <>
                <Button size="small" onClick={() => setCompany({ row, action: 'featured' })}>
                  Featured rank
                </Button>
                <Button size="small" onClick={() => setCompany({ row, action: 'verified' })}>
                  Verification
                </Button>
              </>
            )}
          />
          <HubPager
            page={page}
            pageSize={organizations.data.pageSize}
            total={organizations.data.total}
            onPage={setPage}
          />
        </>
      )}
      </HubSection>
      {/* HUB-UI: badge edits are separate so verifying a company cannot clear its featured rank. */}
      {company && (
        <EntityDialog
          title={company.row.name}
          value={company.action === 'featured' ? { featuredRank: company.row.featuredRank } : { isVerified: company.row.isVerified }}
          fields={
            company.action === 'featured'
              ? [
                  {
                    key: 'featuredRank',
                    label: 'New featured rank',
                    type: 'number',
                    min: 1,
                    helper: 'Enter a rank, or leave empty to remove this company from featured.',
                  },
                ]
              : [{ key: 'isVerified', label: 'Verified', type: 'boolean' }]
          }
          close={() => setCompany(null)}
          save={async (value) => {
            await write('/directory/organizations/' + company.row.id + '/' + company.action, value);
            organizations.reload();
          }}
        />
      )}
    </>
  );
}
